import { Injectable, signal } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { WebcamImage, WebcamInitError } from 'ngx-webcam';
import { HeadshotUploadService } from '../headshot-upload/headshot-upload.service';

@Injectable({
  providedIn: 'root'
})
export class IdUploadService {

  readonly idSide = new BehaviorSubject<Side>('');
  selectedDocType = signal<DocType|"null">("null");
  selectedDocTypeString = signal<string>('');
  readonly selectedFile = new BehaviorSubject(new File([ '' ], ''));

  constructor(
    private readonly headshotService: HeadshotUploadService
  ) { }

  readonly uploadState: UploadState = {
    retake: false,
    showWebcam: false,
    errors: [],
    webcamImage: new BehaviorSubject<WebcamImage | null>(null),
    trigger: new Subject<void>(),
    is_selected: false,
    valid_id: false,
    id_uploading: false,
    submitted: false,
    status: '',
    id_error: '',
  };

  getUploadState(): UploadState {
    return this.uploadState;
  }

  handleInitError(error: WebcamInitError): void {
    this.uploadState.errors.push(error);
  }

  retakePhoto() {
    this.toggleWebcam();
    this.uploadState.retake = true;
    this.uploadState.webcamImage.next(null);
  }

  toggleWebcam(): void {
    this.uploadState.showWebcam = !this.uploadState.showWebcam;
  }

  handleImage(webcamImage: WebcamImage): void {
    this.uploadState.webcamImage.next(webcamImage);
    this.toggleWebcam();
    this.uploadState.retake = false;
  }

  triggerSnapshot(): void {
    this.uploadState.trigger.next();
  }

  resetStatus() {
    this.uploadState.is_selected = false;
    this.uploadState.valid_id = false;
    this.uploadState.id_error = '';
  }

  onRemove() {
    this.selectedFile.next(new File([ '' ], ''));
    this.resetStatus();
  }

  // Convert Headshot image url to blob for uploading to server
  b64toBlob = (b64Data: any, contentType = '', sliceSize = 512) => {
    const byteCharacters = atob(b64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    return new Blob(byteArrays, { type: contentType });
  }

  resetIDVFlow() {
    this.uploadState.retake = false;
    this.uploadState.showWebcam = false;
    this.uploadState.errors = [];
    this.uploadState.webcamImage.next(null);
    this.uploadState.trigger.next();
    this.uploadState.is_selected = false;
    this.uploadState.valid_id = false;
    this.uploadState.id_uploading = false;
    this.uploadState.submitted = false;
    this.uploadState.status = '';
    this.uploadState.id_error = '';

    this.idSide.next('FRONT');
    this.selectedDocType.set("null");
    this.selectedDocTypeString.set('');
    this.selectedFile.next(new File([ '' ], ''));

    this.headshotService.resetHeadshot();

  }

}

export type Side = 'FRONT'| 'BACK' | '';
export type UploadMethod = 'CAMERA' | 'FILE' | 'UNSELECTED';
export type DocType = 'DL' | 'ID' | 'PP' | 'QR';
export const SupportedDocTypes: DocType[] = ['DL', 'ID', 'PP'];
export const SidesForDocType: Map<DocType|"null", number> = new Map([
  ['DL', 2],
  ['ID', 2],
  ['PP', 1],
  ['null', 0]]);
export const NamesForDocType: Map<DocType, string> = new Map([
  ['DL', $localize`Driver's License`],
  ['ID', $localize`Official Identification`],
  ['PP', $localize`Passport`]]);

export interface IdUploadError {
  error_type: string;
  details: {
    reason: string;
    fields?: { key: string, value: string }[];
  };
}

export interface UploadState {
  retake: boolean;
  showWebcam: boolean;
  errors: WebcamInitError[];
  webcamImage: BehaviorSubject<WebcamImage | null>;
  trigger: Subject<void>;
  is_selected: boolean;
  valid_id: boolean;
  id_uploading: boolean;
  submitted: boolean;
  status: '' | 'success' | 'error' | 'rejected' | 'rejected-passportNumber';
  id_error: '' | 'size' | 'type';
}
